import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import Modal, { Field } from './Modal';

/**
 * <ConfirmDialog /> — the one confirmation style for the workspace (replaces
 * window.confirm). Usually reached through `useConfirm()` rather than rendered directly.
 *
 * @param {ReactNode} title
 * @param {ReactNode} [body]
 * @param {string} [confirmLabel='Confirm']   say what happens: "Delete job", not "OK"
 * @param {string} [cancelLabel='Cancel']
 * @param {'default'|'danger'} [tone='default']  danger → red confirm button, focus starts on Cancel
 * @param {()=>void} onConfirm
 * @param {()=>void} onCancel
 * @param {boolean} [busy]                    disables both buttons while an action runs
 */
export const ConfirmDialog = ({
  title, body, confirmLabel = 'Confirm', cancelLabel = 'Cancel', tone = 'default',
  onConfirm, onCancel, busy = false,
}) => {
  const danger = tone === 'danger';
  return (
    <Modal
      role="alertdialog"
      title={title}
      description={typeof body === 'string' ? body : undefined}
      maxWidth={440}
      closeOnBackdrop={!danger}
      onClose={() => { if (!busy && onCancel) onCancel(); }}
      initialFocus={danger ? '[data-cw-cancel]' : '[data-cw-confirm]'}
      footer={(
        <>
          <button type="button" className="cw-btn cw-btn-secondary" data-cw-cancel onClick={onCancel} disabled={busy}>{cancelLabel}</button>
          <button
            type="button"
            className={`cw-btn ${danger ? 'cw-btn-danger' : 'cw-btn-primary'}`}
            data-cw-confirm
            onClick={onConfirm}
            disabled={busy}
            aria-busy={busy || undefined}
          >
            {busy && <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />}
            {confirmLabel}
          </button>
        </>
      )}
    >
      {typeof body === 'string' ? null : body}
    </Modal>
  );
};

/**
 * <PromptDialog /> — single text answer (replaces window.prompt).
 *
 * @param {ReactNode} title
 * @param {ReactNode} [body]
 * @param {string} label                field label (required — inputs need a label)
 * @param {string} [defaultValue='']
 * @param {string} [placeholder]
 * @param {boolean} [required=true]     empty answers are rejected inline
 * @param {'text'|'email'|'number'|'date'|'textarea'} [inputType='text']
 * @param {(value:string)=>string|null} [validate]  return an error message to block submit
 * @param {string} [confirmLabel='Save']
 * @param {'default'|'danger'} [tone]
 * @param {(value:string)=>void} onSubmit
 * @param {()=>void} onCancel
 */
export const PromptDialog = ({
  title, body, label, defaultValue = '', placeholder, required = true, inputType = 'text',
  validate, confirmLabel = 'Save', cancelLabel = 'Cancel', tone = 'default', onSubmit, onCancel,
}) => {
  const [value, setValue] = useState(defaultValue ?? '');
  const [error, setError] = useState(null);
  const submit = (e) => {
    if (e) e.preventDefault();
    const v = String(value ?? '');
    if (required && !v.trim()) { setError(`Enter ${String(label || 'a value').toLowerCase()}`); return; }
    const msg = validate ? validate(v) : null;
    if (msg) { setError(msg); return; }
    onSubmit(v);
  };
  const Input = inputType === 'textarea' ? 'textarea' : 'input';
  return (
    <Modal
      title={title}
      description={typeof body === 'string' ? body : undefined}
      maxWidth={460}
      dirty={String(value ?? '') !== String(defaultValue ?? '')}
      onClose={onCancel}
      footer={(
        <>
          <button type="button" className="cw-btn cw-btn-secondary" onClick={onCancel}>{cancelLabel}</button>
          <button type="submit" form="cw-prompt-form" className={`cw-btn ${tone === 'danger' ? 'cw-btn-danger' : 'cw-btn-primary'}`}>{confirmLabel}</button>
        </>
      )}
    >
      {typeof body === 'string' ? null : body}
      <form id="cw-prompt-form" onSubmit={submit} noValidate>
        <Field label={label} required={required} error={error}>
          <Input
            type={inputType === 'textarea' ? undefined : inputType}
            value={value}
            placeholder={placeholder}
            rows={inputType === 'textarea' ? 4 : undefined}
            onChange={(e) => { setValue(e.target.value); if (error) setError(null); }}
          />
        </Field>
      </form>
    </Modal>
  );
};

/* ── useConfirm(): promise-based confirm / prompt ────────────────────────────── */

const ConfirmContext = createContext(null);

/**
 * Mount once (ContractorWorkspace does) — renders whichever confirm/prompt is pending.
 */
export const ConfirmProvider = ({ children }) => {
  const [pending, setPending] = useState(null);   // { kind, opts, resolve }
  const current = useRef(null);
  const queue = useRef([]);

  const show = useCallback((item) => { current.current = item; setPending(item); }, []);

  const open = useCallback((kind, opts) => new Promise((resolve) => {
    const item = { kind, opts: opts || {}, resolve };
    if (current.current) queue.current.push(item);
    else show(item);
  }), [show]);

  const settle = (value) => {
    const item = current.current;
    if (item) item.resolve(value);
    show(queue.current.shift() || null);
  };

  const api = useMemo(() => ({
    confirm: (opts) => open('confirm', opts),
    prompt: (opts) => open('prompt', opts),
  }), [open]);

  return (
    <ConfirmContext.Provider value={api}>
      {children}
      {pending && pending.kind === 'confirm' && (
        <ConfirmDialog {...pending.opts} onConfirm={() => settle(true)} onCancel={() => settle(false)} />
      )}
      {pending && pending.kind === 'prompt' && (
        <PromptDialog {...pending.opts} onSubmit={(v) => settle(v)} onCancel={() => settle(null)} />
      )}
    </ConfirmContext.Provider>
  );
};

const plainText = (n) => (typeof n === 'string' || typeof n === 'number' ? String(n) : '');

/**
 * const { confirm, prompt } = useConfirm();
 *
 *   if (!(await confirm({ title: 'Delete this job?', body: 'This can't be undone.',
 *                         confirmLabel: 'Delete job', tone: 'danger' }))) return;
 *
 *   const name = await prompt({ title: 'New field', label: 'Field label', defaultValue: '' });
 *   if (name == null) return;          // cancelled
 *
 * confirm → Promise<boolean>; prompt → Promise<string|null>. Outside a
 * ConfirmProvider it falls back to window.confirm / window.prompt.
 */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  return useMemo(() => ctx || {
    confirm: async (o = {}) => window.confirm([plainText(o.title), plainText(o.body)].filter(Boolean).join('\n\n')),
    prompt: async (o = {}) => window.prompt(plainText(o.title) || plainText(o.label), o.defaultValue || ''),
  }, [ctx]);
}

export default ConfirmDialog;
